import React from "react";
import repartisLogo from "../../../assets/images/logo.svg";

// Phase 3 (C2): single source for the A4 page chrome — header strip, content
// area, footer strip — used by every section template. White-label rules are
// applied here so individual templates don't need to know about them:
//   - whiteLabelling === false → repartis logo + footer URL
//   - whiteLabelling === true  → custom per-period logo + neutral footer
// Mirrors the rules already in src/pages/Reports/KeyDiagramView.js so the new
// PDF feels consistent with the existing diagram export.
//
// Sizing (96dpi):
//   A4 portrait  = 794 × 1123 px
//   A4 landscape = 1123 × 794 px
// We render to fixed pixel size and let jsPDF + html-to-image scale on capture.
//
// Props
//   building     { name, address }        building info shown top-left
//   periodLabel  string                   shown under the section title
//   sectionTitle string                   centred at the top
//   whiteLabelling bool                   from tbl_building_time_period
//   logoUrl      string                   per-period custom logo (when whiteLabelling)
//   pageNumber   number                   current page (1-based)
//   totalPages   number                   total page count for this section
//   landscape    bool                     swap dimensions (used by Ponderation matrice in M2)
//   children                              rendered inside the content area

const A4_PORTRAIT  = { width: 794,  height: 1123 };
const A4_LANDSCAPE = { width: 1123, height: 794  };

const A4PageFrame = ({
  building,
  periodLabel,
  sectionTitle,
  whiteLabelling = false,
  logoUrl,
  pageNumber,
  totalPages,
  landscape = false,
  resultsPage = false,
  // When true, the bottom-right "Page X/Y" footer stamp is suppressed. Used by
  // the "Résultat détaillé" section, which is delivered individually to each
  // tenant — the running page number of the full building report would only
  // confuse the recipient (client request).
  hidePageNumber = false,
  children,
}) => {
  const dims = landscape ? A4_LANDSCAPE : A4_PORTRAIT;
  const staticUrl = process.env.REACT_APP_STATIC_URL || "";
  const isWhiteLabel = whiteLabellingEnabled(whiteLabelling);
  const customLogoSrc = isWhiteLabel && logoUrl ? `${staticUrl}${logoUrl}` : null;
  // Three-case logo behaviour, matching the existing Phase 2 KeyDiagramView:
  //   white-label OFF             → default repartis logo
  //   white-label ON + custom     → uploaded custom logo
  //   white-label ON + no custom  → no logo (operator opted out of repartis branding).
  let headerLogo = null;
  // Logo sizing (M1.1 client feedback): default Repartis logo shrunk to ~28px
  // tall to match the offer-template proportions (the screenshot showed 150×28).
  // The white-label custom logo stays at 36 so the operator's own brand reads
  // clearly — they chose that asset, we shouldn't undersize it.
  if (!isWhiteLabel) {
    headerLogo = <img src={repartisLogo} alt="Repartis" style={{ height: 28, width: "auto" }} />;
  } else if (customLogoSrc) {
    headerLogo = <img src={customLogoSrc} alt="Logo" style={{ height: 36, width: "auto" }} />;
  }

  return (
    <div
      data-rr-page
      // M2 Résultats pages carry this extra marker so the PDF exporter can capture
      // them in a LATER phase (after the key-diagram graphs), matching the
      // reference page order. M1 pages don't set it.
      {...(resultsPage ? { "data-rr-results-page": "" } : {})}
      className="repartition-page"
      style={{
        width: dims.width,
        height: dims.height,
        boxSizing: "border-box",
        backgroundColor: "#ffffff",
        color: "#00033F",
        // font-family is set via the .repartition-page class in
        // repartition-fonts.css so PP Neue Montreal cascades into every nested
        // node (tables, charts) without us having to thread fontFamily through
        // each template.
        display: "flex",
        flexDirection: "column",
        position: "relative",
        padding: 0,
        margin: 0,
      }}
    >
      {/* HEADER — FIXED height so the flex:1 content area below is deterministic
          for every building (1- vs 2-line address) and identical between any
          render and the live app. This is the fix for the recurring last-row
          clip: the app's global `body{line-height:24px}` (assets/css/style.css)
          used to inflate this band unpredictably. We pin the band height AND an
          explicit line-height on each text node so nothing inherits the 24px. */}
      <div
        data-rr-header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "20px 32px 16px 32px",
          flexShrink: 0,
          height: 96,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#00033F", lineHeight: "22px" }}>
            {building?.name || ""}
          </div>
          {building?.address && (
            <div style={{ fontSize: 10, color: "#4a4c6b", marginTop: 2, lineHeight: "13px", whiteSpace: "pre-line" }}>
              {building.address}
            </div>
          )}
        </div>

        {/* CENTER: fixed report title + period. Client reference PDF shows
            this same title/period pair on every non-cover page — the varying
            section name moves below the frame into the content area. */}
        <div style={{ textAlign: "center", flex: 1, padding: "0 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#00033F", lineHeight: "16px" }}>
            Répartition des frais d&apos;exploitation et d&apos;entretien
          </div>
          {periodLabel && (
            <div style={{ fontSize: 10, color: "#4a4c6b", marginTop: 4, lineHeight: "13px" }}>
              Période d&apos;exercice : {periodLabel}
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0 }}>{headerLogo}</div>
      </div>

      {/* CONTENT — section title rendered left-aligned inside the content area
          (matches client reference PDF: "Résultat détaillé", "Résultats par
          objet", etc. sit above the section body, not inside the header). */}
      <div
        data-rr-content
        style={{
          flex: 1,
          padding: "8px 32px",
          overflow: "hidden",
          fontSize: 10,
          color: "#00033F",
        }}
      >
        {/* Section title kept vertically tight: fontSize 10, no bottom margin,
            line-height 12 → consumes ~12px of the content area. Any bigger
            and the deterministic-row-height M1 tables (Coûts 24 rows/page,
            Compteurs 48 rows/page) would push their last row off the frame
            (M1.5b regression). Left-aligned to match the client PDF layout. */}
        {sectionTitle && (
          <div style={{ fontSize: 10, fontWeight: 700, color: "#00033F", lineHeight: "12px", marginBottom: 2 }}>
            {sectionTitle}
          </div>
        )}
        {children}
      </div>

      {/* FOOTER — FIXED height (same rationale as the header): keeps the flex:1
          content area constant regardless of the inherited body line-height. */}
      <div
        data-rr-footer
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 32px 14px 32px",
          borderTop: "1px solid rgba(0,3,63,0.1)",
          fontSize: 9,
          color: "#4a4c6b",
          flexShrink: 0,
          height: 60,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {whiteLabellingEnabled(whiteLabelling) ? (
          <span>Powered by core repartis</span>
        ) : (
          <span style={{ whiteSpace: "pre-line", lineHeight: "12px" }}>
            repartis SA{"\n"}Avenue Villamont 17, 1005 Lausanne{"\n"}+41 58 255 43 43 — info@repartis.ch
          </span>
        )}
        <span style={{ fontWeight: 600 }}>
          {whiteLabellingEnabled(whiteLabelling) ? "" : "repartis.ch"}
          {hidePageNumber
            ? ""
            : `${whiteLabellingEnabled(whiteLabelling) ? "" : " | "}${
                totalPages > 1 ? `Page ${pageNumber}/${totalPages}` : `Page ${pageNumber || 1}`
              }`}
        </span>
      </div>
    </div>
  );
};

// True only when the period explicitly opts in; null / undefined → default repartis branding.
function whiteLabellingEnabled(v) {
  return v === true || v === "true";
}

export default A4PageFrame;
